'use client';

import React, { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/ui/AppIcon';

export default function ContactPage() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch('/api/submit-contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                alert(result.message);
                // Clear form on success
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                alert(result.error || 'Failed to submit contact form. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting contact form:', error);
            alert('An unexpected error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">Contact Us</h1>
                        <p className="text-xl text-muted-foreground">We'd love to hear from you</p>
                    </div>
                </section>

                <section className="py-20 px-6">
                    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
                        {/* Contact Info */}
                        <div>
                            <h2 className="text-2xl font-headline font-bold text-foreground mb-6">Get in Touch</h2>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <Icon name="EnvelopeIcon" size={24} variant="outline" className="text-primary flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-foreground mb-1">Email</div>
                                        <a href="mailto:info@nile-tex.com" className="text-muted-foreground hover:text-primary">info@nile-tex.com</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Icon name="PhoneIcon" size={24} variant="outline" className="text-primary flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-foreground mb-1">Phone</div>
                                        <a href="tel:+201044868081" className="text-muted-foreground hover:text-primary">+20 10 44868081</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Icon name="MapPinIcon" size={24} variant="outline" className="text-primary flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-foreground mb-1">Address</div>
                                        <div className="text-muted-foreground">Cairo, Egypt</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="bg-card border border-border rounded-lg p-8">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-foreground mb-2">Name</label>
                                    <input id="contact-name" name="name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-foreground mb-2">Email</label>
                                    <input id="contact-email" name="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-foreground mb-2">Subject</label>
                                    <input id="contact-subject" name="subject" type="text" required value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full px-4 py-3 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-foreground mb-2">Message</label>
                                    <textarea id="contact-message" name="message" required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full px-4 py-3 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
